import React, { useCallback, useContext, useMemo, useState } from 'react';
import { ViewSubcategoryContext, IViewSubcategoryContext } from '../viewSubcategoryState/viewSubcategoryContext';
import { Link } from 'react-router-dom';
import { RequestEntity, EntityStatus } from 'types';
import { useTranslation } from 'react-i18next';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import ImgUploaderByEntityId from 'components/ImgUploader/ImgUploaderByEntityId';
import { ViewPageBlock } from 'components/ViewPage/ViewPageBlock';
import { ViewPageSection } from 'components/ViewPage/ViewPageSection';
import { ItemList } from 'components/ViewPage//ItemList';
import { NoItemsNotification } from 'components/ViewPage/NoItemsNotification';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import { MenuTranslationForm } from 'components/TranslationForm/MenuTranslationForm';
import Button from 'components/Button/Button';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { ISelectOption } from 'components/Select/types';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import breadcrumbs from 'images/breadcrumbs.svg';
import style from './ViewForm.module.scss';

export const ViewForm: React.FC = () => {
	const {
		setTranslationTab,
		changeTypeOfModalWindow,
		setRestaurantIdForRestaurantProps,
		subcategoryFullInfo,
		accessibleRestaurants,
		selectedTranslationTab,
		restaurantIdForRestaurantProps,
		restaurantProps,
	} = useContext<IViewSubcategoryContext>(ViewSubcategoryContext);
	const { t } = useTranslation('form');
	const hasSchedulePriority = subcategoryFullInfo?.overrideScheduleAvailability;
	const schedulePriorityValue = hasSchedulePriority ? t('yes') : t('no');
	const listOfRestaurants = useMemo(() => accessibleRestaurants, [accessibleRestaurants]);
	const availableForRepetition = restaurantProps?.availableForRepetition;
	const availableForRepetitionValue = availableForRepetition ? t('yes') : t('no');
	const hideSubcategory = restaurantProps?.isHidden;
	const hideSubcategoryValue = hideSubcategory ? t('yes') : t('no');
	const listOfDishes = useMemo(() => restaurantProps?.dishes || [], [restaurantProps?.dishes]);
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);

	const mappedRestaurantOptions = useMemo<ISelectOption[]>(() => {
		const visibleItems = showArchived
			? accessibleRestaurants
			: accessibleRestaurants.filter(
					(restaurant) =>
						restaurant.status === EntityStatus.Active || restaurant.id === restaurantIdForRestaurantProps,
			  );

		return visibleItems.map((restaurant) => ({
			...restaurant,
			additionalContent: restaurant.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [accessibleRestaurants, showArchived, restaurantIdForRestaurantProps]);

	const handleClickAssignRestaurant = useCallback(
		() => changeTypeOfModalWindow('assignRestaurants'),
		[changeTypeOfModalWindow],
	);

	const handleClickAssignDishes = useCallback(
		() => changeTypeOfModalWindow('assignDishes'),
		[changeTypeOfModalWindow],
	);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	return (
		<div className={style.container} data-test="ViewForm">
			<div className={style.content}>
				<ViewPageBlock heading="generalPropertiesHeading">
					<ViewPageSection heading="mainInfoHeading">
						<div className={style.row}>
							<div className={style.imgWrapper}>
								{subcategoryFullInfo?.id && subcategoryFullInfo.photoPath ? (
									<ImgUploaderByEntityId
										resource={RequestEntity.SubcategoriesMenuV2}
										entityID={subcategoryFullInfo.id}
										isMenuV2={true}
										photoSize="Medium"
										loaderSize="big"
										key="1"
									/>
								) : (
									<ImgUploaderByEntityId
										resource={RequestEntity.SubcategoriesMenuV2}
										entityID=""
										isMenuV2={true}
										photoSize="Medium"
										loaderSize="big"
										key="2"
									/>
								)}
							</div>
							<div className={style.mainInfoInputs}>
								<FullInfoField label="nameSubcategoryLabel" value={subcategoryFullInfo?.name || '-'} />
								<FullInfoField
									label="customerLabel"
									value={subcategoryFullInfo?.customer.name || '-'}
								/>
								<FullInfoField
									label="categoryLabel"
									value={subcategoryFullInfo?.category.name || '-'}
								/>
							</div>
						</div>
					</ViewPageSection>

					<ViewPageSection heading="translationsHeading">
						<MenuTranslationForm
							className={style.translationForm}
							setTranslationTab={setTranslationTab}
							isShowLanguageName={false}
							IDSelectedCustomer={subcategoryFullInfo?.customer.id || ''}
							translations={subcategoryFullInfo?.translations || []}
							selectedTranslationTab={selectedTranslationTab}
							nameLabel="nameSubcategoryLabel"
						/>
					</ViewPageSection>

					<ViewPageSection heading="scheduleHeading">
						<FullInfoField
							label="hasSchedulePriorityLabel"
							value={schedulePriorityValue}
							prefixIcon="asCheckbox"
							prefixIconName={hasSchedulePriority ? 'checkMark' : 'crossMark'}
							prefixIconIsChecked={hasSchedulePriority}
						/>
						<div className={style.row}>
							<FullInfoField
								label="activationScheduleLabel"
								value={subcategoryFullInfo?.activationCron || '-'}
							/>
							<FullInfoField
								label="deactivationScheduleLabel"
								value={subcategoryFullInfo?.deactivationCron || '-'}
							/>
						</div>
					</ViewPageSection>

					<ViewPageSection heading="restaurantsHeading">
						{listOfRestaurants.length ? (
							<ItemList
								itemListLabel="allRestaurantsWithThisSubcategory"
								items={listOfRestaurants}
								pathToItem="/clients/restaurants?restaurantFullInfoID="
								assignBtnTranslationKey="editRestaurantsList"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						) : (
							<NoItemsNotification
								title="noRestaurantsAssigned"
								description="assignRestaurantsInTheCustomerSubcategoryToBeAbleToEditThem"
								assignBtnTranslationKey="assignRestaurantsButton"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						)}
					</ViewPageSection>

					<div className={style.btnWrapper} data-test="BtnBlock1">
						{/* <Button theme="primaryBlue" disabled={true} onClick={() => console.log('Check tree view')}>
							<Text translationNameSpace="form" translationKey="checkTreeViewButton" asFragment={true} />
						</Button> */}
						<Link
							target={`blank_${Date.now()}`}
							rel="noopener noreferrer"
							to={`/menu-v2/subcategory/edit?subcategoryID=${subcategoryFullInfo?.id}`}>
							<Button
								className={style.btn}
								theme="primaryBlue"
								icon={
									<svg className={style.icon}>
										<use xlinkHref={`${breadcrumbs}#pencil`} />
									</svg>
								}>
								<Text translationNameSpace="form" translationKey="editButton" asFragment={true} />
							</Button>
						</Link>
					</div>
				</ViewPageBlock>

				<ViewPageBlock heading="restaurantPropertiesHeading">
					{accessibleRestaurants.length ? (
						<>
							<OutsideFormSelect
								testId="restaurantProperties"
								label="restaurantsLabel"
								name="restaurantProperties"
								value={restaurantIdForRestaurantProps}
								options={mappedRestaurantOptions}
								onChange={setRestaurantIdForRestaurantProps}
								keepSearchWithDropdownRender={true}
								dropdownRender={renderSelectMenu}
							/>

							<ViewPageSection heading="dishesHeading">
								{restaurantIdForRestaurantProps && !!listOfDishes.length && (
									<ItemList
										items={listOfDishes}
										pathToItem="/menu-v2/dishes/edit?dishID="
										assignBtnTranslationKey="editDishListButton"
										onClickShowAssignModal={handleClickAssignDishes}
									/>
								)}
								{restaurantIdForRestaurantProps && !listOfDishes.length && (
									<NoItemsNotification
										title="noDishesAssigned"
										description="assignDishesToCustomersSubcategoryToBeAbleToEditThem"
										assignBtnTranslationKey="assignDishesButton"
										onClickShowAssignModal={handleClickAssignDishes}
									/>
								)}
								{!restaurantIdForRestaurantProps && (
									<Text translationNameSpace="form" translationKey="-" />
								)}
							</ViewPageSection>

							<ViewPageSection heading="scheduleHeading">
								<div className={style.row}>
									<FullInfoField
										containerClassName={style.marginTopZero}
										label="activationScheduleLabel"
										value={restaurantProps?.activationCron || '-'}
									/>
									<FullInfoField
										containerClassName={style.marginTopZero}
										label="deactivationScheduleLabel"
										value={restaurantProps?.deactivationCron || '-'}
									/>
								</div>
							</ViewPageSection>
						</>
					) : (
						<ViewPageSection heading="restaurantsHeading">
							<NoItemsNotification
								title="noRestaurantsAssigned"
								description="assignRestaurantsInTheCustomerSubcategoryToBeAbleToEditThem"
								assignBtnTranslationKey="assignRestaurantsButton"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						</ViewPageSection>
					)}

					<div className={style.row}>
						<ViewPageSection heading="availableForRepetitionHeading">
							{restaurantProps ? (
								<FullInfoField
									label="availableForRepetitionLabel"
									value={availableForRepetitionValue}
									prefixIcon="asCheckbox"
									prefixIconName={availableForRepetition ? 'checkMark' : 'crossMark'}
									prefixIconIsChecked={availableForRepetition}
								/>
							) : (
								<FullInfoField label="availableForRepetitionLabel" value="-" />
							)}
						</ViewPageSection>
						<ViewPageSection heading="hideSubcategoryHeading">
							{restaurantProps ? (
								<FullInfoField
									label="hideSubcategoryLabel"
									value={hideSubcategoryValue}
									prefixIcon="asCheckbox"
									prefixIconName={hideSubcategory ? 'checkMark' : 'crossMark'}
									prefixIconIsChecked={hideSubcategory}
								/>
							) : (
								<FullInfoField label="hideSubcategoryLabel" value="-" />
							)}
						</ViewPageSection>
					</div>

					{restaurantIdForRestaurantProps && (
						<div className={style.btnWrapper} data-test="BtnBlock2">
							<Link
								target={`blank_${Date.now()}`}
								rel="noopener noreferrer"
								to={`/menu-v2/subcategory/edit-restaurant-properties?subcategoryID=${
									subcategoryFullInfo?.id || ''
								}`}>
								<Button
									className={style.btn}
									theme="primaryBlue"
									icon={
										<svg className={style.icon}>
											<use xlinkHref={`${breadcrumbs}#pencil`} />
										</svg>
									}>
									<Text translationNameSpace="form" translationKey="editButton" asFragment={true} />
								</Button>
							</Link>
						</div>
					)}
				</ViewPageBlock>
			</div>
		</div>
	);
};
